import {
  BookOpen,
  CalendarDays,
  Church,
  Contact,
  Cross,
  Globe2,
  HandHeart,
  HeartHandshake,
  Home,
  Images,
  MapPin,
  Mic2,
  Radio,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import {
  formatServiceTime,
  generateNextOccurrence,
  getPublicServiceOccurrences,
} from "./serviceSchedule";
import { buildPublicGivingUrl } from "../services/giving/publicGivingResolver";
import { getPublicOrgGalleryImages } from "./publicGallery";
import { buildOrgPath } from "./canonicalPublicRoutes";
import { darkenColor, isDarkColor } from "./orgThemeTokens";
import { resolveGivingConfig } from "./publicGivingConfig";

export const ministryItems = [
  {
    key: "worship-prayer",
    label: "Worship & Prayer",
    icon: "church",
    description: "Prayer, worship gatherings, and the Word.",
  },
  {
    key: "children-youth",
    label: "Children & Youth",
    icon: "users",
    description: "Raising the next generation in Christ.",
  },
  {
    key: "outreach-care",
    label: "Outreach & Care",
    icon: "heartHandshake",
    description: "Care, compassion, and community support.",
  },
  {
    key: "global-missions",
    label: "Global Missions",
    icon: "globe",
    description: "Gospel work across nations and communities.",
  },
  {
    key: "discipleship",
    label: "Discipleship",
    icon: "bookOpen",
    description: "Bible study, mentoring, and leadership growth.",
  },
];

export const navIconMap = {
  bookOpen: BookOpen,
  calendar: CalendarDays,
  church: Church,
  contact: Contact,
  cross: Cross,
  gallery: Images,
  globe: Globe2,
  handHeart: HandHeart,
  heartHandshake: HeartHandshake,
  home: Home,
  mapPin: MapPin,
  mic: Mic2,
  radio: Radio,
  shield: ShieldCheck,
  sparkles: Sparkles,
  users: Users,
};

export function cleanText(value, fallback = "") {
  if (Array.isArray(value)) {
    const joined = value.map((item) => cleanDisplayText(item, "")).filter(Boolean).join(", ");
    return joined || fallback;
  }
  if (value && typeof value === "object") {
    const text =
      cleanDisplayText(value.label, "") ||
      cleanDisplayText(value.name, "") ||
      cleanDisplayText(value.displayName, "") ||
      cleanDisplayText(value.title, "") ||
      cleanDisplayText(value.serviceArea, "") ||
      cleanDisplayText(value.address, "") ||
      cleanDisplayText(value.publicAddress, "");
    return text || fallback;
  }
  const text = String(value || "").trim();
  if (!text) return fallback;
  if (/^(undefined|null|nan)$/i.test(text)) return fallback;
  if (/invalid date/i.test(text)) return fallback;
  if (/[\u00C3\uFFFD]/.test(text)) return fallback;
  return text;
}

export function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

// An event belongs on "Upcoming" surfaces only until it has passed. Events
// without a parseable date are kept — the admin may list undated gatherings.
// A dated event stays visible through the end of its final day.
export function isUpcomingPublicEvent(event = {}, now = new Date()) {
  const rawDate = String(event?.endDate || event?.startDate || event?.date || "").trim();
  if (!rawDate) return true;
  const parsed = new Date(`${rawDate.slice(0, 10)}T23:59:59`);
  if (Number.isNaN(parsed.getTime())) return true;
  return parsed.getTime() >= now.getTime();
}

export function cleanDisplayText(value, fallback = "") {
  if (value == null) return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return cleanText(value, fallback);
  }
  if (Array.isArray(value)) {
    return cleanText(value.map((item) => cleanDisplayText(item, "")).filter(Boolean).join(", "), fallback);
  }
  if (typeof value === "object") {
    const address = [
      value.addressLine1,
      value.addressLine2,
      value.street,
      value.city,
      value.state || value.stateRegion,
      value.postalCode || value.zip,
      value.country,
    ]
      .map((item) => cleanDisplayText(item, ""))
      .filter(Boolean)
      .join(", ");
    if (address) return address;

    return cleanText(
      value.label ||
        value.name ||
        value.displayName ||
        value.serviceArea ||
        value.address ||
        value.publicAddress,
      fallback
    );
  }
  return fallback;
}

export function orgBasePath(orgSlug) {
  const slug = encodeURIComponent(cleanText(orgSlug, ""));
  return slug ? `/${slug}` : "";
}

function firstBoolean(sources = [], fallback = true) {
  for (const value of sources) {
    if (typeof value === "boolean") return value;
  }
  return fallback;
}

export function getPageEnabled(publicProfile = {}, key, fallback = true) {
  const page = publicProfile?.pageContent?.[key];
  return typeof page?.enabled === "boolean" ? page.enabled : fallback;
}

export function getLayoutEnabled(publicProfile = {}, key, fallback = true) {
  const item = safeArray(publicProfile?.homepageLayout).find((section) => section?.key === key);
  return typeof item?.enabled === "boolean" ? item.enabled : fallback;
}

export function getNavEnabled(publicProfile = {}, keys = [], fallback = true) {
  const nav = publicProfile?.navigationConfig || {};
  const site = publicProfile?.siteSections || {};
  return firstBoolean(
    keys.flatMap((key) => [nav?.[key], site?.[key]]),
    fallback
  );
}

export function hasArrayContent(...values) {
  return values.some((value) => Array.isArray(value) && value.some(Boolean));
}

function pageExplicitlyEnabled(publicProfile = {}, key) {
  return publicProfile?.pageContent?.[key]?.enabled === true;
}

export function hasMediaMessages(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  const mediaContent = publicProfile.pageContent?.media || {};
  const hasMessages = hasArrayContent(
    mediaPage.latestMessages,
    mediaPage.messages,
    mediaPage.sermons,
    mediaContent.latestMessages,
    mediaContent.messages,
    publicProfile.latestMessages,
    publicProfile.sermons,
    publicProfile.messages
  );
  if (hasMessages) return mediaPage.sections?.showLatestMessages !== false;
  return pageExplicitlyEnabled(publicProfile, "media") && mediaPage.sections?.showLatestMessages === true;
}

export function hasWatchLive(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  const liveUrl = cleanText(
    mediaPage.livestreamUrl ||
      publicProfile.livestreamUrl ||
      publicProfile.liveUrl,
    ""
  );
  return Boolean(liveUrl) || (pageExplicitlyEnabled(publicProfile, "media") && mediaPage.sections?.showLivestream === true);
}

export function hasTestimonies(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  const page = publicProfile.pageContent?.testimonies || {};
  return hasArrayContent(
    mediaPage.testimonies,
    page.items,
    page.stories,
    page.testimonies,
    publicProfile.testimonies
  ) || page.enabled === true;
}

export function hasMediaSeries(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  return (
    mediaPage.sections?.showSeries !== false &&
    hasArrayContent(mediaPage.featuredSeries, mediaPage.series, publicProfile.mediaSeries)
  );
}

export function hasMediaDevotionals(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  return (
    mediaPage.sections?.showDevotionals !== false &&
    hasArrayContent(mediaPage.devotionalResources, mediaPage.devotionals, publicProfile.devotionalResources)
  );
}

export function hasMediaAudioLinks(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  if (mediaPage.sections?.showAudio === false) return false;
  return Boolean(cleanText(mediaPage.podcastUrl || mediaPage.spotifyUrl || mediaPage.applePodcastsUrl, ""));
}

export function hasMediaArchiveLink(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  return Boolean(cleanText(mediaPage.archiveCtaUrl || mediaPage.archiveUrl, ""));
}

export function hasAnyMediaDestination(publicProfile = {}) {
  const mediaPage = publicProfile.mediaPage || {};
  return (
    mediaPage.enabled === true ||
    pageExplicitlyEnabled(publicProfile, "media") ||
    hasWatchLive(publicProfile) ||
    hasMediaMessages(publicProfile) ||
    hasTestimonies(publicProfile) ||
    hasMediaSeries(publicProfile) ||
    hasMediaDevotionals(publicProfile) ||
    hasMediaAudioLinks(publicProfile) ||
    hasMediaArchiveLink(publicProfile)
  );
}

export function hasPublicGallery(publicProfile = {}) {
  if (getPageEnabled(publicProfile, "gallery", true) === false) return false;
  return getPublicOrgGalleryImages({ publicProfile }).length > 0 || pageExplicitlyEnabled(publicProfile, "gallery");
}

export function isPublicPageAvailable(publicProfile = {}, key, fallback = true) {
  if (getPageEnabled(publicProfile, key, fallback) === false) return false;
  if (key === "about") return getNavEnabled(publicProfile, ["showAbout"], true);
  if (key === "leadership") return getNavEnabled(publicProfile, ["showLeadership", "showLeader"], true);
  if (key === "ministries") {
    return getNavEnabled(publicProfile, ["showMinistries", "showPrograms"], true);
  }
  if (key === "services") return getNavEnabled(publicProfile, ["showServices", "showServiceTimes"], true);
  if (key === "media") {
    return getNavEnabled(publicProfile, ["showMedia"], true) && hasAnyMediaDestination(publicProfile);
  }
  if (key === "gallery") {
    return (
      getNavEnabled(publicProfile, ["showGallery"], true) &&
      hasPublicGallery(publicProfile)
    );
  }
  if (key === "events") {
    return getNavEnabled(publicProfile, ["showEvents"], true);
  }
  if (key === "campaigns") return true;
  if (key === "giving") return getNavEnabled(publicProfile, ["showGiving", "showGive"], true);
  if (key === "contact") return getNavEnabled(publicProfile, ["showContact"], true);
  return true;
}

export function isPublicNavItemEnabled(item, publicProfile = {}) {
  const partner = publicProfile?.partnerWithUs || {};
  const givingConfig = resolveGivingConfig(publicProfile, { publicProfile });
  const enabledGivingKeys = new Set(safeArray(givingConfig.categories).map((category) => category.key));
  const hasGivingDestination = (key) => enabledGivingKeys.has(key);
  const hasRoutineGivingDestination = () =>
    hasGivingDestination("tithe") || hasGivingDestination("offering") || hasGivingDestination("general");
  const key = item?.key || "";
  const checks = {
    about: () => getNavEnabled(publicProfile, ["showAbout"], true),
    "new-here": () => isPublicPageAvailable(publicProfile, "newHere", true),
    beliefs: () => isPublicPageAvailable(publicProfile, "beliefs", true),
    leadership: () => getNavEnabled(publicProfile, ["showLeadership", "showLeader"], true),
    ministries: () =>
      getNavEnabled(publicProfile, ["showMinistries", "showPrograms"], true),
    bible: () => getNavEnabled(publicProfile, ["showBible"], true),
    media: () =>
      getNavEnabled(publicProfile, ["showMedia"], true) &&
      isPublicPageAvailable(publicProfile, "media", true),
    "watch-live": () => isPublicPageAvailable(publicProfile, "media", true) && hasWatchLive(publicProfile),
    sermons: () => isPublicPageAvailable(publicProfile, "media", true) && hasMediaMessages(publicProfile),
    testimonies: () => isPublicPageAvailable(publicProfile, "media", true) && hasTestimonies(publicProfile),
    gallery: () =>
      getNavEnabled(publicProfile, ["showGallery"], true) &&
      hasPublicGallery(publicProfile),
    services: () => getNavEnabled(publicProfile, ["showServices", "showServiceTimes"], true),
    events: () =>
      getNavEnabled(publicProfile, ["showEvents"], true) &&
      isPublicPageAvailable(publicProfile, "events", true),
    give: () =>
      getNavEnabled(publicProfile, ["showGiving", "showGive"], true) &&
      isPublicPageAvailable(publicProfile, "giving", true) &&
      enabledGivingKeys.size > 0,
    "give-now": () => isPublicPageAvailable(publicProfile, "giving", true) && hasRoutineGivingDestination(),
    "tithes-offering": () =>
      partner.enableTithesOffering !== false &&
      (hasGivingDestination("tithe") || hasGivingDestination("offering")),
    "campaign-giving": () =>
      partner.enableCampaignGiving !== false &&
      partner.enableCampaigns !== false &&
      hasGivingDestination("campaign") &&
          isPublicPageAvailable(publicProfile, "campaigns", true),
    "general-giving": () =>
      partner.enableGeneralGiving !== false &&
      partner.enableGeneral !== false &&
      hasGivingDestination("general"),
    contact: () => isPublicPageAvailable(publicProfile, "contact", true),
    "contact-us": () => isPublicPageAvailable(publicProfile, "contact", true),
    "plan-your-visit": () => isPublicPageAvailable(publicProfile, "newHere", true),
    "prayer-request": () => isPublicPageAvailable(publicProfile, "prayerRequest", true),
  };

  return typeof checks[key] === "function" ? checks[key]() : true;
}

function filterPublicNavItems(items = [], publicProfile = {}) {
  return safeArray(items)
    .filter((item) => isPublicNavItemEnabled(item, publicProfile))
    .map((item) => {
      const children = Array.isArray(item.children)
        ? filterPublicNavItems(item.children, publicProfile)
        : item.children;
      return { ...item, children };
    })
    .filter((item) => !Array.isArray(item.children) || item.children.length > 0 || item.href);
}

export function buildDonateUrl(organizationId, params = {}) {
  const id = cleanText(organizationId, "");
  if (!id) return "";
  return buildPublicGivingUrl({
    orgSlug: id,
    transactionPurpose: params.transactionPurpose || "giving",
    givingCategory: params.givingCategory || params.category || "general",
    campaignSlug: params.campaignSlug || params.campaignId,
    fundraiserSlug: params.fundraiserSlug || params.participantSlug,
    donationRouteId: params.donationRouteId || params.routeId,
    source: params.source || params.sourceContext || "public_site",
    amount: params.amount,
    currency: params.currency,
    interval: params.interval,
    pathOptions: params.pathOptions || {},
  });
}

export function buildPublicNavItems({
  orgSlug,
  organizationId,
  ministries,
  onDonate,
  onGivingCategory,
  publicProfile,
  pathOptions,
} = {}) {
  const orgPath = (path) => buildOrgPath(orgSlug, path, pathOptions || {});
  const donateBase = organizationId || orgSlug;
  const donateNow = buildDonateUrl(donateBase, { pathOptions });
  const titheUrl = buildDonateUrl(donateBase, { givingCategory: "tithe", pathOptions });
  const generalUrl = buildDonateUrl(donateBase, { givingCategory: "general", pathOptions });
  const ministryNavItems = safeArray(ministries)
    .filter(Boolean)
    .map((item, index) => ({
        key: item.slug || item.key || `ministry-${index + 1}`,
        // Canonical ministry documents store the admin-entered name under
        // `name`; publicProfile entries use `title`. Read both so the nav
        // never falls back to "Ministry N" placeholders for real data.
        label: item.title || item.label || item.name || `Ministry ${index + 1}`,
        icon: item.iconKey || item.icon || "church",
        description: item.description || "",
        href: orgPath(`/ministries/${item.slug || item.key || `ministry-${index + 1}`}`),
      }));

  const donateItem = (key, label, href, category, description, icon) => ({
    key,
    label,
    href,
    description,
    icon,
    onClick:
      typeof onGivingCategory === "function" && category
        ? () => onGivingCategory(category, href)
        : typeof onDonate === "function" && key === "give-now"
          ? onDonate
          : null,
  });

  const items = [
    {
      key: "about",
      label: "About",
      children: [
        {
          key: "new-here",
          label: "New Here",
          href: orgPath("/new-here"),
          description: "Plan your first Sunday with us.",
          icon: "home",
        },
        {
          key: "beliefs",
          label: "What We Believe",
          href: orgPath("/beliefs"),
          description: "Our faith, mission, and foundation.",
          icon: "cross",
        },
        {
          key: "leadership",
          label: "Leadership",
          href: orgPath("/leadership"),
          description: "Meet the pastors and ministry team.",
          icon: "users",
        },
      ],
    },
    {
      key: "ministries",
      label: "Ministries",
      href: orgPath("/ministries"),
      children: ministryNavItems,
    },
    {
      key: "bible",
      label: "Bible",
      href: orgPath("/bible"),
    },
    {
      key: "media",
      label: "Media",
      children: [
        {
          key: "bible",
          label: "Bible",
          href: orgPath("/bible"),
          description: "Read and reflect on scripture.",
          icon: "bookOpen",
        },
        {
          key: "watch-live",
          label: "Watch Live",
          href: orgPath("/media/watch-live"),
          description: "Worship with us online.",
          icon: "radio",
        },
        {
          key: "sermons",
          label: "Sermons",
          href: orgPath("/messages"),
          description: "Watch recent sermons from our church.",
          icon: "mic",
        },
        {
          key: "gallery",
          label: "Gallery",
          href: orgPath("/gallery"),
          description: "Moments from our church family.",
          icon: "gallery",
        },
      ],
    },
    {
      key: "services",
      label: "Services",
      href: orgPath("/services"),
    },
    {
      key: "events",
      label: "Events",
      href: orgPath("/events"),
    },
    {
      key: "give",
      label: "Give",
      children: [
        donateItem(
          "give-now",
          "Give Now",
          donateNow,
          null,
          "Open the secure giving flow.",
          "handHeart"
        ),
        donateItem(
          "tithes-offering",
          "Tithes & Offering",
          titheUrl,
          "tithe",
          "Faithful stewardship and worship.",
          "sparkles"
        ),
        {
          key: "campaign-giving",
          label: "Campaign Giving",
          href: orgPath("/campaigns"),
          description: "Support specific ministry initiatives.",
          icon: "heartHandshake",
        },
        donateItem(
          "general-giving",
          "General Giving",
          generalUrl,
          "general",
          "Give where it is needed most.",
          "handHeart"
        ),
      ],
    },
    {
      key: "contact",
      label: "Contact",
      children: [
        {
          key: "contact-us",
          label: "Contact",
          href: orgPath("/contact"),
          description: "Reach the church office.",
          icon: "contact",
        },
        {
          key: "plan-your-visit",
          label: "Plan Your Visit",
          href: orgPath("/new-here"),
          description: "Service time, location, and expectations.",
          icon: "mapPin",
        },
        {
          key: "prayer-request",
          label: "Prayer Request",
          href: orgPath("/prayer-request"),
          description: "Let us pray with you.",
          icon: "heartHandshake",
        },
      ],
    },
  ];

  return filterPublicNavItems(items, publicProfile || {});
}

// Flat church-template mobile menu. Give, Events, and Media stay out on
// purpose: Give is the header CTA, and Events/Media already have mobile homes
// in the bottom nav and the More menu. Items missing from `items` (tenant
// gating) stay hidden here too.
export function buildMobileMenuItems({ items, orgSlug, pathOptions } = {}) {
  const source = Array.isArray(items) ? items.filter(Boolean) : [];
  const orgPath = (path) => buildOrgPath(orgSlug, path, pathOptions || {});
  const byKey = (key) =>
    source.find((item) => item?.key === key || item?.label === key) || null;
  const childOf = (item, key) =>
    Array.isArray(item?.children)
      ? item.children.find((child) => child?.key === key || child?.label === key) || null
      : null;

  const about = byKey("about");
  const contact = byKey("contact");
  const newHere = childOf(about, "new-here") || childOf(contact, "plan-your-visit");

  const flatten = (item, fallbackHref, overrides = {}) =>
    item
      ? { ...item, children: [], href: item.href || fallbackHref || "", ...overrides }
      : null;

  return [
    flatten(newHere, orgPath("/new-here"), { key: "new-here", label: "New Here" }),
    flatten(about, orgPath("/about")),
    flatten(byKey("ministries"), orgPath("/ministries")),
    flatten(byKey("services"), orgPath("/services")),
    flatten(byKey("bible"), orgPath("/bible")),
    flatten(contact, orgPath("/contact")),
  ].filter(Boolean);
}

export function getPublicProfile(org) {
  return org?.publicProfile || org?.profile || {};
}

export function getOrgName(org) {
  const profile = getPublicProfile(org);
  // publicProfile.displayName is the canonical name the website builder
  // writes; the root organizationName mirror and church/legacy aliases stay
  // as fallbacks so old records keep rendering (Phase 1B contract fix).
  return cleanText(
    profile.displayName ||
      profile.organizationName ||
      profile.churchName ||
      org?.organizationName ||
      org?.churchName ||
      org?.name,
    "Church"
  );
}

export function getPublicOrgDisplayName(org) {
  const profile = getPublicProfile(org);
  const slugName = cleanText(org?.slug || org?.orgSlug || "", "")
    .replace(/-\d+$/g, "")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return cleanText(
    profile.publicShortName ||
      org?.publicShortName ||
      profile.shortName ||
      org?.shortName ||
      profile.displayName ||
      org?.displayName ||
      org?.organizationName ||
      org?.name ||
      slugName,
    "Organization"
  );
}

export function getOrgLogo(org) {
  const profile = getPublicProfile(org);
  return cleanText(profile.logoUrl || profile.logo || org?.logoUrl || org?.logo, "");
}

export function getOrgTagline(org) {
  const profile = getPublicProfile(org);
  return cleanText(
    profile.tagline ||
      profile.missionStatement ||
      org?.tagline ||
      org?.missionStatement,
    "A church family centered on worship, discipleship, and mission."
  );
}

export function getOrgAddress(org) {
  const profile = getPublicProfile(org);
  const contact = profile.contactInfo || {};

  // The Website Admin's "display address publicly" toggle is an explicit
  // privacy opt-out. When it is false, every public surface must show only
  // the locality (city / state / country) — never street-level details.
  const addressHiddenPublicly =
    profile.displayAddressPublicly === false ||
    org?.location?.displayAddressPublicly === false ||
    org?.displayAddressPublicly === false;
  if (addressHiddenPublicly) {
    const locality = [
      profile.city || org?.location?.city || org?.city,
      profile.stateRegion || org?.location?.state || org?.state,
      profile.country || org?.location?.country || org?.country,
    ]
      .map((item) => cleanDisplayText(item, ""))
      .filter(Boolean)
      .join(", ");
    return cleanDisplayText(locality, "");
  }

  const profileAddress = [
    profile.addressLine1,
    profile.addressLine2,
    profile.city,
    profile.stateRegion,
    profile.postalCode,
    profile.country,
  ].filter(Boolean).join(", ");
  const candidates = [
    profile.address,
    profile.location,
    profile.serviceLocation,
    contact.address,
    contact.publicAddress,
    contact.location,
    profileAddress,
    org?.address,
    org?.location,
    org?.serviceLocation,
    [org?.street, org?.city, org?.state, org?.zip].filter(Boolean).join(", "),
  ];
  return cleanDisplayText(candidates.find((item) => cleanDisplayText(item, "")), "");
}

export function getOrgEmail(org) {
  const profile = getPublicProfile(org);
  const contact = profile.contactInfo || {};
  return cleanText(contact.publicEmail || profile.emailPublic || profile.email || profile.contactEmail || org?.email || org?.contactEmail, "");
}

export function getPrayerRequestEmail(org) {
  const profile = getPublicProfile(org);
  const contact = profile.contactInfo || {};
  // The admin form historically accepted non-email values (e.g. a phone
  // number) in prayerRequestEmail; a mailto: built from those is broken.
  // Only ever return an email-shaped value, falling through to the next
  // candidate otherwise.
  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value, ""));
  const candidates = [
    contact.prayerRequestEmail,
    contact.publicEmail,
    profile.prayerRequestEmail,
    profile.emailPublic,
    profile.email,
    profile.contactEmail,
    org?.prayerRequestEmail,
    org?.email,
    org?.contactEmail,
  ];
  return cleanText(candidates.find(isEmail), "");
}

export function getOrgPhone(org) {
  const profile = getPublicProfile(org);
  const contact = profile.contactInfo || {};
  return cleanText(contact.publicPhone || profile.phone || profile.contactPhone || org?.phone || org?.contactPhone, "");
}

export function getServiceTime(org) {
  const occurrences = getPublicServiceOccurrences(org, { days: 45 });
  if (occurrences.length) {
    const firstOccurrence = formatServiceTime(occurrences[0]);
    return cleanText(
      `${firstOccurrence.title} • ${firstOccurrence.frequencyLabel}`,
      "Service time coming soon"
    );
  }

  const profile = getPublicProfile(org);
  const serviceTimes = safeArray(profile.serviceTimes || org?.serviceTimes);
  const first = serviceTimes[0];
  if (typeof first === "string") return cleanText(first, "Service time coming soon");
  if (first) {
    const day = cleanText(first.day || first.label, "Service");
    const time = cleanText(first.time || first.startTime, "");
    return cleanText([day, time].filter(Boolean).join(" • "), "Service time coming soon");
  }

  return cleanText(
    profile.serviceTime ||
      profile.worshipTime ||
      org?.serviceTime ||
      org?.worshipTime ||
      org?.attendanceSettings?.serviceTime,
    "Service time coming soon"
  );
}

function normalizeServiceTemplateKey(service = {}) {
  const schedule = service.schedule || {};
  const explicit = cleanText(
    service.serviceTemplateId || service.templateId || service.templateKey || service.key,
    ""
  );
  if (explicit) return explicit.toLowerCase();
  const title = cleanText(service.title || service.name || service.serviceType, "service")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const frequency = cleanText(service.frequency || schedule.frequency, schedule.isRecurring === false ? "one-time" : "weekly");
  const dayPart = frequency === "monthly"
    ? `day-${Number(schedule.dayOfMonth || service.dayOfMonth || 1)}`
    : frequency === "one-time"
      ? cleanText(schedule.date || service.date, "one-time")
      : `weekday-${Number(schedule.weekday ?? service.weekday ?? 0)}`;
  const startTime = cleanText(schedule.startTime || service.startTime, "10:00");
  return `${title}|${frequency}|${dayPart}|${startTime}`;
}

function normalizeServiceScheduleSignature(service = {}) {
  const schedule = service.schedule || {};
  const title = cleanText(service.title || service.name || service.serviceType, "service")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const frequency = cleanText(service.frequency || schedule.frequency, schedule.isRecurring === false ? "one-time" : "weekly");
  const dayPart = frequency === "monthly"
    ? `day-${Number(schedule.dayOfMonth || service.dayOfMonth || 1)}`
    : frequency === "one-time"
      ? cleanText(schedule.date || service.date, "one-time")
      : `weekday-${Number(schedule.weekday ?? service.weekday ?? 0)}`;
  const startTime = cleanText(schedule.startTime || service.startTime, "10:00");
  return `${title}|${frequency}|${dayPart}|${startTime}`;
}

function isGeneratedServiceInstance(service = {}) {
  const typeText = `${service.recordType || ""} ${service.kind || ""} ${service.sourceType || ""}`.toLowerCase();
  return (
    typeText.includes("occurrence") ||
    service.isGeneratedOccurrence === true ||
    service.generatedFromTemplate === true ||
    Boolean(service.occurrenceId || service.occurrenceDate || service.templateOccurrenceId || service.serviceDate || service.startsAt)
  );
}

function dedupePublicServiceTemplates(services = []) {
  const seen = new Set();
  const signatures = new Set();
  return safeArray(services)
    .filter((service) => service?.archived !== true && service?.status !== "archived")
    .filter((service) => service?.visibility?.isPublic !== false && service?.visibility?.showOnPublicWebsite !== false)
    .filter((service) => cleanText(service?.title || service?.name, "").toLowerCase() !== "service")
    .sort((a, b) => {
      const aGenerated = isGeneratedServiceInstance(a);
      const bGenerated = isGeneratedServiceInstance(b);
      if (aGenerated !== bGenerated) return aGenerated ? 1 : -1;
      return 0;
    })
    .filter((service) => {
      const key = normalizeServiceTemplateKey(service);
      const signature = normalizeServiceScheduleSignature(service);
      if (seen.has(key) || signatures.has(signature)) return false;
      seen.add(key);
      signatures.add(signature);
      return true;
    });
}

export function getServiceTimes(org, options = {}) {
  // Canonical service templates are the single source of truth for the
  // schedule (the same source the Next Service card and services page use).
  // Generated instance documents are only a fallback for orgs that have no
  // canonical templates, so every surface shows one consistent schedule in
  // the organization's timezone.
  const canonicalOccurrences = getPublicServiceOccurrences(org, {
    days: options.days || 45,
  });
  if (canonicalOccurrences.length) {
    const seen = new Set();
    return canonicalOccurrences
      .map(formatServiceTime)
      .filter((item) => {
        const key = `${item.title}|${item.frequencyLabel}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, options.limit || 6);
  }

  const serviceDocs = dedupePublicServiceTemplates(org?.services);
  if (serviceDocs.length) {
    return serviceDocs
      .map((service) => {
        const next = generateNextOccurrence(service, {
          days: options.days || 365,
          orgTimeZone: org?.timeZone || org?.publicProfile?.timeZone,
        });
        if (!next) return null;
        return {
          title: cleanText(service.title || service.name, "Service"),
          frequencyLabel: next.label,
          timeLabel: next.formattedTime,
          dateLabel: next.formattedDate,
          date: next.date,
          locationLabel: cleanText(service.location?.name || service.location?.address, getOrgAddress(org) || ""),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, options.limit || 6);
  }

  const occurrences = getPublicServiceOccurrences(org, {
    days: options.days || 45,
  });
  if (occurrences.length) {
    const seen = new Set();
    return occurrences
      .map(formatServiceTime)
      .filter((item) => {
        const key = `${item.title}|${item.frequencyLabel}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, options.limit || 6);
  }

  const legacy = getServiceTime(org);
  return legacy
    ? [
        {
          title: "Service Time",
          frequencyLabel: legacy,
          locationLabel: getOrgAddress(org) || "",
        },
      ]
    : [];
}

export function getHeroImage(org) {
  const profile = getPublicProfile(org);
  // No stock-photo fallback: an org without an uploaded hero resolves to ""
  // and public surfaces render getHeroFallbackBackground instead (Phase 1B).
  return cleanText(
    profile.heroImage ||
      profile.heroImageUrl ||
      org?.heroImage ||
      org?.heroImageUrl,
    ""
  );
}

// The warm dark-charcoal gradient HeroSection has always used as its built-in
// no-image background. Shared so every public hero surface falls back the
// same way.
export const NEUTRAL_HERO_FALLBACK_BACKGROUND =
  "linear-gradient(135deg, #15120f 0%, #302414 100%)";

function validHexColor(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw) ? raw : "";
}

/**
 * Brand-aware hero fallback for orgs with no uploaded hero image or slides.
 * Hero text is white, so every stop must stay dark: a light primary color is
 * darkened harder (0.5/0.35) than a dark one (0.35/none). Invalid or missing
 * brand colors fall back to the neutral gradient.
 */
export function getHeroFallbackBackground(branding = {}) {
  const primary = validHexColor(branding?.primaryColor);
  if (!primary) return NEUTRAL_HERO_FALLBACK_BACKGROUND;

  const primaryIsDark = isDarkColor(primary);
  const start = darkenColor(primary, primaryIsDark ? 0.35 : 0.5);
  const mid = darkenColor(primary, primaryIsDark ? 0 : 0.35);

  const secondary = validHexColor(branding?.secondaryColor);
  const end = secondary
    ? darkenColor(secondary, isDarkColor(secondary) ? 0.15 : 0.4)
    : mid;

  return `linear-gradient(135deg, ${start} 0%, ${mid} 60%, ${end} 100%)`;
}

export function getGalleryImages(org) {
  return getPublicOrgGalleryImages(org);
}

function parsePublicEventDate(value) {
  if (!value) return null;
  if (value?.toDate) {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      const [, year, month, day] = isoDateOnly;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatEventDate(value) {
  if (!value) return { label: "Upcoming", month: "", day: "" };
  const date = parsePublicEventDate(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return { label: "Upcoming", month: "", day: "" };
  }
  return {
    label: date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    month: date.toLocaleDateString(undefined, { month: "short" }),
    day: date.toLocaleDateString(undefined, { day: "2-digit" }),
  };
}

export function formatEventDateRange(event = {}) {
  const startValue =
    event?.startDate ||
    event?.date ||
    event?.eventDate ||
    event?.startsAt ||
    event?.startAt ||
    null;
  const endValue = event?.endDate || event?.endsAt || event?.endAt || null;
  const start = parsePublicEventDate(startValue);
  const end = parsePublicEventDate(endValue);

  if (!start) return { label: "Upcoming", month: "", day: "" };
  if (!end || start.toDateString() === end.toDateString()) {
    return formatEventDate(start);
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const startMonth = start.toLocaleDateString(undefined, { month: "long" });
  const endMonth = end.toLocaleDateString(undefined, { month: "long" });
  const startDay = start.toLocaleDateString(undefined, { day: "numeric" });
  const endDay = end.toLocaleDateString(undefined, { day: "numeric" });
  const startYear = start.toLocaleDateString(undefined, { year: "numeric" });
  const endYear = end.toLocaleDateString(undefined, { year: "numeric" });

  const label = sameMonth
    ? `${startMonth} ${startDay}–${endDay}, ${startYear}`
    : sameYear
      ? `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${startYear}`
      : `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;

  return {
    label,
    month: start.toLocaleDateString(undefined, { month: "short" }),
    day: start.toLocaleDateString(undefined, { day: "2-digit" }),
  };
}
